import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnitLinks } from 'core/contexts/unitLinks';
import { UnitType, validateRequiredString } from '@utriper/core';
import { Chip, Switch } from '@utriper/core-ui';
import { Grid2, IconButton, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';

const UnitCard = ({ id, name, type, unitId, scopeId, description, capacity, minPerBooking, maxPerBooking, isActive, handleOpenForm = () => {} }) => {
    const { i18n, t } = useTranslation();
    const translate = (key, options) => t(`experience_units.card.${key}`, options);
    const { updateExperienceLink } = useUnitLinks();
    const [submitting, setSubmitting] = useState();

    const unitTypeLabel = useMemo(() => {
        if (!Number.isInteger(type)) return '';

        return UnitType.getTranslatedName(UnitType.types, type, i18n.language);
    }, [type, i18n.language]);

    // TODO: MOVE TO ANOTHER FILE :)
    const Text = ({ label }) => {
        return (
            <Grid2
                size={12}
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                <Typography fontSize={14} fontWeight={500} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                    {label}
                </Typography>
            </Grid2>
        );
    };

    const handleCheckIsActive = async (event) => {
        try {
            setSubmitting(true);
            const checked = event?.target?.checked;
            await updateExperienceLink(scopeId, { id, isActive: checked });
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Grid2
            container
            size={{ xs: 12, sm: 6, md: 3 }}
            direction="column"
            sx={{
                padding: '1em',
                borderRadius: '.5em',
                border: 'solid #13131340 1px',
                boxShadow: 'var(--shadow-card)',
                backgroundColor: '#fff',
                gap: '1em',
                transition: 'all .2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 4px 20px 0px rgba(0,0,0,0.05)',
                },
            }}
        >
            <Grid2 container size={12} spacing={1.5} direction="column">
                {/* Name and Type */}
                <Grid2
                    container
                    size={12}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {/* Name */}
                    <Typography fontSize={20} fontWeight={700} sx={{ color: '#131313', maxWidth: '100%' }} noWrap>
                        {name}
                    </Typography>

                    {/* Type and Edit button */}
                    <Grid2 container alignItems="center" gap="8px">
                        <Chip label={unitTypeLabel} sx={{ background: '#137DC540', color: '#137DC5', padding: '8px 4px', borderRadius: '4px' }} />
                        <IconButton
                            size="small"
                            onClick={() => {
                                const unitData = {
                                    id,
                                    name,
                                    type,
                                    description,
                                    capacity,
                                    minPerBooking,
                                    maxPerBooking,
                                    isActive,
                                    unitId,
                                };
                                handleOpenForm(true, unitData);
                            }}
                        >
                            <Edit sx={{ color: '#131313' }} />
                        </IconButton>
                    </Grid2>
                </Grid2>

                {/* Description */}
                {validateRequiredString(description, null) && <Text label={description} />}

                {/* Capacity */}
                <Text label={translate('capacity', { capacity: Number.isInteger(capacity) ? `${capacity} ${translate('people')}` : translate('unlimited') })} />

                {/* Max & Min per booking */}
                {Number.isInteger(minPerBooking) && <Text label={translate('min_per_booking', { minPerBooking })} />}
                {Number.isInteger(maxPerBooking) && <Text label={translate('max_per_booking', { maxPerBooking })} />}

                {/* Is Active Switch */}
                <Grid2>
                    <Switch name="isActive" checked={isActive} onChange={handleCheckIsActive} label={translate('is_active')} disabled={submitting} />
                </Grid2>
            </Grid2>
        </Grid2>
    );
};

export default UnitCard;
